import * as React from 'react';
import classNames from 'classnames';
import { compose } from 'redux';
import withStyles, { WithStyles } from 'react-jss';
import Icon from '../../../../Components/Icon';
import { scrollIntoView } from '../../../../Utilities/ScrollTo';

export interface IListProgressItemProps {
    /** Item title */
    title: React.ReactNode;
    /** Is current progress item */
    current?: boolean;
    /** Is completed progress item */
    completed?: boolean;
    /** Make content toggleable by clicking on the header */
    toggleable?: boolean;
    /** Default toggleable state */
    defaultOpen?: boolean;
    /** Disable scrolling on activation */
    disableScrolling?: boolean;
}

interface IState {
    isOpen: boolean;
}

type Props = IListProgressItemProps & WithStyles<typeof styles>;

/**
 * Basic list element with hover background. You should wrap this inside ListBasic component.
 */
class ListProgressItem extends React.Component<Props, IState> {
    static displayName: string = 'ListProgressItem';

    private node;

    constructor (props: Props) {
        super(props);
        this.state = {
            isOpen: !!this.props.defaultOpen,
        };
    }

    componentDidUpdate (prevProps: Readonly<Props>, prevState: Readonly<IState>, snapshot?: any): void {
        if (!prevProps.current && this.props.current && this.node && !this.props.disableScrolling) {
            window.setTimeout(() => {
                this.node.scrollIntoView();
            }, 10);
        }
    }

    render () {
        return (
            <li
                ref={(node) => this.node = node}
                className={classNames('list-progress-item', {
                    current: this.props.current,
                    completed: this.props.completed,
                }, this.props.classes.item)}
            >
                <div
                    className={classNames('list-progress-item-title', {
                        toggleable: this.props.toggleable && !this.props.current && !this.props.completed,
                    }, this.props.classes.title)}
                    onClick={this.props.toggleable ? (e) => {
                        e.preventDefault();
                        this.setState((prevState) => ({ isOpen: !prevState.isOpen }));
                    } : undefined}
                >
                    {this.props.completed && (
                        <Icon width={16} height={16} kind="check"/>
                    )}
                    <span className="text">{this.props.title}</span>
                </div>
                {((this.props.current || this.props.completed) || (this.props.toggleable && this.state.isOpen)) && (
                    <div className={classNames('list-progress-item-content', this.props.classes.content)}>
                        {this.props.children}
                    </div>
                )}
            </li>
        );
    }
}

const styles = (theme) => {
    return {
        title: {
            'backgroundColor': theme.colors.gray.lighter,
            '&:before': {
                backgroundColor: theme.colors.gray.light,
            },
        },
        content: {},
        item: {
            '&.completed $title': {
                '&:before': {
                    color: theme.colors.white,
                    backgroundColor: theme.colors.secondary,
                },
            },
            '&.current $title': {
                'backgroundColor': theme.colors.components?.listProgressItem?.activeBackground || theme.colors.gray.light,
                '&:before': {
                    color: theme.colors.white,
                    backgroundColor: theme.colors.primary,
                },
            },
        },
    };
};

export default compose<React.ComponentClass<IListProgressItemProps>>(
    withStyles(styles, { injectTheme: true })
)(ListProgressItem);
